import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { listNodes } from '@/api/nodes'
import { listJobs, getJob, cancelJob } from '@/api/jobs'
import { usePolling } from '@/hooks/usePolling'
import type { JobSummary, JobDetail, NodeSummary } from '@/types'
import JobTable from '@/components/JobTable'
import LogTail from '@/components/LogTail'
import JobStatusBadge from '@/components/JobStatusBadge'
import Pagination from '@/components/Pagination'
import { getJobNodeDisplayName, getJobNodeSecondaryText } from '@/utils/jobs'
import { formatKstDateTime } from '@/utils/datetime'
import { useAuth } from '@/auth/useAuth'

// Polling intervals
const ACTIVE_POLL_MS = 10000
const IDLE_POLL_MS = 30000
const PAGE_SIZE = 10
const NODE_FILTER_LIMIT = 1000

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'timed_out', label: 'Timed out' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'recording', label: 'Recording' },
  { value: 'upload', label: 'Upload' },
]

const RANGE_OPTIONS = [
  { value: '1d', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
]

const ACTIVE_STATUSES = new Set(['queued', 'running'])

function parsePage(value: string | null): number {
  const parsed = Number(value ?? '1')
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1
}

function startedFromForRange(range: string): string | undefined {
  if (range === 'all') return undefined
  const days = range === '1d' ? 1 : range === '30d' ? 30 : 7
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date.toISOString()
}

function formatTime(ts: string | null | undefined): string {
  return formatKstDateTime(ts, '—')
}

function formatElapsed(startedAt: string | null | undefined): string {
  if (!startedAt) return '—'
  const diff = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)
  if (diff < 60) return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`
}

export default function JobsPage() {
  const { hasPermission } = useAuth()
  const canCancelJobs = hasPermission('job:cancel')
  const [jobs, setJobs] = useState<JobSummary[]>([])
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const statusFilter = searchParams.get('status') ?? ''
  const typeFilter = searchParams.get('type') ?? ''
  const nodeFilter = searchParams.get('node_id') ?? ''
  const rawRangeFilter = searchParams.get('range') ?? '7d'
  const rangeFilter = RANGE_OPTIONS.some((option) => option.value === rawRangeFilter) ? rawRangeFilter : '7d'
  const page = useMemo(() => parsePage(searchParams.get('page')), [searchParams])

  // Detail modal
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  // Cancel confirm
  const [cancelTarget, setCancelTarget] = useState<JobSummary | null>(null)
  const [cancelling, setCancelling] = useState(false)

  // Toast
  const [toast, setToast] = useState<string | null>(null)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const hasActiveJobs = jobs.some((j) => ACTIVE_STATUSES.has(j.status))
  const pollInterval = hasActiveJobs ? ACTIVE_POLL_MS : IDLE_POLL_MS

  const updateQuery = useCallback((key: 'status' | 'type' | 'node_id' | 'range', value: string) => {
    const nextParams = new URLSearchParams(searchParams)
    const normalizedValue = value.trim()
    if (normalizedValue && !(key === 'range' && normalizedValue === '7d')) {
      nextParams.set(key, normalizedValue)
    } else {
      nextParams.delete(key)
    }
    nextParams.delete('page')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const setPage = useCallback((nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams)
    if (nextPage <= 1) {
      nextParams.delete('page')
    } else {
      nextParams.set('page', String(nextPage))
    }
    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  const fetchJobs = useCallback(async () => {
    try {
      const params: {
        node_id?: string
        status?: string
        type?: string
        started_from?: string
        limit: number
        offset: number
      } = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }
      if (nodeFilter) params.node_id = nodeFilter
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.type = typeFilter
      const startedFrom = startedFromForRange(rangeFilter)
      if (startedFrom) params.started_from = startedFrom
      const res = await listJobs(params)
      setJobs(res.items)
      setTotal(res.total)
      setError(null)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }, [nodeFilter, page, rangeFilter, statusFilter, typeFilter])

  useEffect(() => {
    setLoading(true)
    fetchJobs()
  }, [fetchJobs])

  // Load node list for the node filter dropdown
  useEffect(() => {
    listNodes({ limit: NODE_FILTER_LIMIT }).then((res) => setNodes(res.items)).catch(() => {})
  }, [])

  usePolling(fetchJobs, pollInterval, !loading)

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (!loading && page > totalPages) {
      setPage(totalPages)
    }
  }, [loading, page, setPage, total])

  // Jobs already filtered server-side (type, status, node_id all sent as query params)
  const displayedJobs = jobs
  const selectedJobNodeDisplay = selectedJob ? getJobNodeDisplayName(selectedJob) : null
  const selectedJobNodeSecondary = selectedJob ? getJobNodeSecondaryText(selectedJob) : null
  const selectedJobNodeGroup =
    selectedJobNodeSecondary && selectedJobNodeSecondary !== selectedJob?.node_key
      ? selectedJobNodeSecondary
      : null

  async function openDetail(job: JobSummary) {
    setDetailLoading(true)
    setDetailError(null)
    try {
      const detail = await getJob(job.id)
      setSelectedJob(detail)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setDetailError(err?.message ?? 'Failed to load job detail')
      setSelectedJob({ ...job, log_tail: null, pid: null } as JobDetail)
    } finally {
      setDetailLoading(false)
    }
  }

  async function confirmCancel() {
    if (!cancelTarget) return
    setCancelling(true)
    try {
      await cancelJob(cancelTarget.id)
      showToast(`Job ${cancelTarget.id.slice(0, 8)}... cancellation requested`)
      setCancelTarget(null)
      await fetchJobs()
    } catch (e: unknown) {
      const err = e as { message?: string }
      showToast(`Cancel failed: ${err?.message ?? 'unknown error'}`)
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>Jobs</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={statusFilter}
          onChange={(e) => updateQuery('status', e.target.value)}
          style={selectStyle}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => updateQuery('type', e.target.value)}
          style={selectStyle}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={nodeFilter}
          onChange={(e) => updateQuery('node_id', e.target.value)}
          style={selectStyle}
        >
          <option value="">All nodes</option>
          {nodes.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name ?? n.node_key} ({n.site ?? '—'})
            </option>
          ))}
        </select>

        <select
          value={rangeFilter}
          onChange={(e) => updateQuery('range', e.target.value)}
          style={selectStyle}
        >
          {RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
          {total.toLocaleString()} job{total !== 1 ? 's' : ''}
          {hasActiveJobs ? ' — polling every 10s' : ' — polling every 30s'}
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div style={errBannerStyle}>
          {error}
          <button onClick={fetchJobs} style={{ marginLeft: 12, fontSize: 12, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {/* Job table */}
      <div style={cardStyle}>
        <JobTable
          jobs={displayedJobs}
          onRowClick={openDetail}
          onCancel={canCancelJobs ? (job) => setCancelTarget(job) : undefined}
          loading={loading}
        />
        {!loading && !error && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            itemLabel="jobs"
            onPageChange={setPage}
          />
        )}
      </div>

      {/* Job detail modal */}
      {(selectedJob || detailLoading) && (
        <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget) { setSelectedJob(null); setDetailError(null) } }}>
          <div style={modalStyle} role="dialog" aria-modal="true">
            <div style={modalHeaderStyle}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Job Detail</span>
              <button
                onClick={() => { setSelectedJob(null); setDetailError(null) }}
                style={closeBtnStyle}
                aria-label="Close"
              >
                x
              </button>
            </div>

            {detailLoading ? (
              <p style={{ color: '#888', fontSize: 13 }}>Loading...</p>
            ) : selectedJob ? (
              <div>
                {detailError && (
                  <div style={{ color: '#c62828', fontSize: 12, marginBottom: 10 }}>
                    {detailError}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginBottom: 16 }}>
                  <DetailRow label="Job ID" value={selectedJob.id} mono />
                  <DetailRow label="Node" value={selectedJobNodeDisplay} />
                  <DetailRow label="Node ID" value={selectedJob.node_id} mono />
                  {selectedJob.node_key && (
                    <DetailRow label="Node key" value={selectedJob.node_key} mono />
                  )}
                  {selectedJobNodeGroup && (
                    <DetailRow label="Node group" value={selectedJobNodeGroup} />
                  )}
                  <DetailRow label="Type" value={selectedJob.type} />
                  <DetailRow label="Camera" value={selectedJob.camera_key ?? '—'} mono />
                  <DetailRow
                    label="Status"
                    value={<JobStatusBadge status={selectedJob.status} />}
                  />
                  <DetailRow label="Started" value={formatTime(selectedJob.started_at)} />
                  <DetailRow label="Finished" value={formatTime(selectedJob.finished_at)} />
                  {selectedJob.started_at && !selectedJob.finished_at && (
                    <DetailRow label="Elapsed" value={formatElapsed(selectedJob.started_at)} />
                  )}
                  {selectedJob.pid != null && (
                    <DetailRow label="PID" value={String(selectedJob.pid)} mono />
                  )}
                  {selectedJob.progress != null && (
                    <DetailRow label="Progress" value={`${selectedJob.progress.toFixed(0)}%`} />
                  )}
                  {selectedJob.config_revision != null && (
                    <DetailRow label="Config revision" value={String(selectedJob.config_revision)} mono />
                  )}
                  {selectedJob.command_id && (
                    <DetailRow label="Command ID" value={selectedJob.command_id} mono />
                  )}
                  {selectedJob.requested_by != null && (
                    <DetailRow label="Requested by" value={selectedJob.requested_by} />
                  )}
                </div>

                {selectedJob.error_message != null && (
                  <ErrorMessageBlock message={selectedJob.error_message} />
                )}

                {selectedJob.progress != null && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Progress</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${Math.min(selectedJob.progress, 100)}%`,
                            height: '100%',
                            background: selectedJob.status === 'failed' ? '#c62828' : '#3949ab',
                            borderRadius: 4,
                            transition: 'width 0.3s',
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 12, color: '#555', minWidth: 36 }}>
                        {selectedJob.progress.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}

                <LogTail log={selectedJob.log_tail} label="Log tail" />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Cancel confirm dialog */}
      {cancelTarget && (
        <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget && !cancelling) setCancelTarget(null) }}>
          <div style={{ ...modalStyle, width: 360 }} role="dialog" aria-modal="true">
            <div style={modalHeaderStyle}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Cancel Job</span>
            </div>
            <p style={{ fontSize: 14, color: '#444', marginBottom: 20 }}>
              Cancel job <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{cancelTarget.id.slice(0, 12)}...</code>?
              <br />
              <span style={{ fontSize: 12, color: '#888' }}>
                Type: {cancelTarget.type} — Status: {cancelTarget.status}
              </span>
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCancelTarget(null)}
                style={secondaryBtnStyle}
                disabled={cancelling}
              >
                Keep
              </button>
              <button
                onClick={confirmCancel}
                style={dangerBtnStyle}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={toastStyle}>{toast}</div>
      )}
    </div>
  )
}

function DetailRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#222', fontFamily: mono ? 'monospace' : undefined, overflowWrap: 'anywhere' }}>
        {value ?? '—'}
      </div>
    </div>
  )
}

function ErrorMessageBlock({ message }: { message: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0 }}>
        Error
      </div>
      <div style={errorMessageBlockStyle}>
        {message}
      </div>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '16px',
}

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  cursor: 'pointer',
}

const errBannerStyle: React.CSSProperties = {
  color: '#c62828',
  background: '#fdecea',
  padding: '10px 14px',
  borderRadius: 4,
  marginBottom: 16,
  fontSize: 13,
}

const errorMessageBlockStyle: React.CSSProperties = {
  color: '#9f1d1d',
  background: '#fff5f5',
  border: '1px solid #f2c7c7',
  borderRadius: 4,
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  maxHeight: 180,
  overflowY: 'auto',
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: '20px 24px',
  width: 560,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
}

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: '1px solid #e0e0e0',
}

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 16,
  color: '#888',
  padding: '2px 6px',
  fontFamily: 'inherit',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#c62828',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  background: '#1a1a2e',
  color: '#fff',
  padding: '10px 16px',
  borderRadius: 6,
  fontSize: 13,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  zIndex: 2000,
}
